import { Badge, Flex } from "@mantine/core";
import { IconChevronRight, IconX } from "@tabler/icons-react";

import { type ICol } from "../../../../interfaces";
import { useDispatch } from "../../../../redux/hooks";
import { toggleHeaderGroupCollapse } from "../../../../redux/slices/generatorSlice";
import { StyledTh } from "../../../../styled";

interface Props {
  headers?: ICol[];
  sectionIndex: number;
}

// Persistent control bar listing every collapsible split-header group in a
// section, so a group can be collapsed or re-expanded from here at any time.
const CollapsedHeaderGroups = ({ headers, sectionIndex }: Props) => {
  const dispatch = useDispatch();

  const collapsibleGroups = (headers ?? [])
    .map((header, headerIndex) => ({ header, headerIndex }))
    .filter(({ header }) => header.type === "split" && header.collapseColumn);

  if (collapsibleGroups.length === 0) return null;

  const handleToggle = (headerIndex: number) => {
    // No setEditing(true) here — collapse/expand is a view preference, not
    // a data edit, and shouldn't mark the entry as dirty/trigger a save.
    dispatch(toggleHeaderGroupCollapse({ sectionIndex, headerIndex }));
  };

  return (
    <thead>
      <tr>
        <StyledTh colSpan={100} style={{ padding: 0 }}>
          <Flex
            gap={8}
            wrap="wrap"
            style={{
              padding: "4px 10px",
              position: "sticky",
              left: 0,
            }}>
            {collapsibleGroups.map(({ header, headerIndex }) => (
              <Badge
                key={headerIndex}
                className="collapsible-header-cell legacyLight"
                variant={header.collapsed ? "outline" : "light"}
                color={header.collapsed ? undefined : "gray"}
                radius="sm"
                size="lg"
                rightSection={
                  header.collapsed ? (
                    <IconChevronRight size={14} />
                  ) : (
                    <IconX size={14} />
                  )
                }
                style={{
                  cursor: "pointer",
                  textTransform: "none",
                  ...(header.collapsed && {
                    color: "var(--mantine-color-thColor-0)",
                    borderColor: "var(--mantine-color-thColor-0)",
                  }),
                }}
                onClick={() => handleToggle(headerIndex)}
                title={
                  header.collapsed
                    ? `Expand ${header.name}`
                    : `Collapse ${header.name}`
                }>
                {header.name}
              </Badge>
            ))}
          </Flex>
        </StyledTh>
      </tr>
    </thead>
  );
};

export default CollapsedHeaderGroups;
